import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Printer, Download, BookOpenCheck, Pencil, ChevronRight } from 'lucide-react';

/**
 * Training Library — One-Page Launch Summary
 * A printable/shareable snapshot for Dhiraj to present at morning briefing.
 */
export default function TrainingLaunchSummary() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        // Fetch SOPs for every MindEscapes outlet in parallel
        const outlets = [
          'Stays By MindEscapes',
          'Celeste By MindEscapes (KETTI)',
          'ANMA By MindEscapes (COONOOR)',
          'ANMA By MindEscapes (OOTY)',
          'XIN By MindEscapes HSR Layout',
          'XIN By MindEscapes Koramangala',
        ];
        const results = await Promise.all(
          outlets.map((o) => api.get(`/outlet-intel/${encodeURIComponent(o)}/sops`).then((r) => ({ outlet: o, ...r.data })).catch(() => null))
        );
        if (cancelled) return;
        // Aggregate: unique module titles, total records, categories
        const allSops = results.filter(Boolean).flatMap((r) => r.sops || []);
        const uniqueTitles = new Set(allSops.map((s) => s.title));
        const cats = {};
        allSops.forEach((s) => { cats[s.category || 'Uncategorised'] = (cats[s.category || 'Uncategorised'] || 0) + 1; });
        const mediaCount = allSops.reduce((sum, s) => sum + (s.media?.length || 0), 0);
        const perOutlet = results.filter(Boolean).map((r) => ({
          name: r.outlet,
          total: r.summary?.total || 0,
        }));
        setData({
          uniqueModules: uniqueTitles.size,
          totalRecords: allSops.length,
          mediaCount,
          categories: Object.entries(cats).sort((a, b) => b[1] - a[1]),
          perOutlet,
        });
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) {
    return <div className="p-8 text-sm" style={{ color: '#6B7280' }}>Loading launch summary...</div>;
  }

  const downloadCsv = () => {
    const today = new Date().toISOString().slice(0, 10);
    const rows = [];
    rows.push(['MindEscapes® Training Library — Launch Summary']);
    rows.push([`Generated`, new Date().toLocaleString('en-IN')]);
    rows.push([]);
    rows.push(['Headline Stats']);
    rows.push(['Metric', 'Value']);
    rows.push(['Unique Modules', data.uniqueModules]);
    rows.push(['Total SOP Records', data.totalRecords]);
    rows.push(['Outlets Covered', data.perOutlet.length]);
    rows.push(['Media Attachments', data.mediaCount]);
    rows.push([]);
    rows.push(['SOPs By Category']);
    rows.push(['Category', 'Count']);
    data.categories.forEach(([cat, n]) => rows.push([cat, n]));
    rows.push([]);
    rows.push(['SOPs Per Outlet']);
    rows.push(['Outlet', 'SOP Count']);
    data.perOutlet.forEach((o) => rows.push([o.name, o.total]));
    const csv = rows.map((r) => r.map((c) => {
      const s = String(c ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `training_launch_summary_${today}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 print:p-0" data-testid="training-launch-summary">
      {/* Action Bar (hidden on print) */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1B2A4A' }}>Training Library — Launch Summary</h1>
          <p className="text-xs" style={{ color: '#6B7280' }}>One-page overview for Dhiraj's morning briefing</p>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadCsv} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border" style={{ borderColor: '#2A7B88', color: '#2A7B88', backgroundColor: 'white' }} data-testid="download-launch-summary-btn">
            <Download size={14} /> Download CSV
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="print-launch-summary-btn">
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Printable page */}
      <div className="bg-white rounded-xl border p-8 shadow-sm" style={{ borderColor: '#E5E1DC' }}>
        {/* Editor access callout — hidden on print */}
        <div className="print:hidden mb-6 rounded-lg border p-4 flex items-start justify-between gap-4" style={{ borderColor: '#2A7B88', backgroundColor: '#F0FAFB' }} data-testid="editor-callout">
          <div className="flex items-start gap-3">
            <Pencil size={18} style={{ color: '#2A7B88' }} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Want to add, edit, or delete articles?</p>
              <p className="text-[12px] mt-0.5" style={{ color: '#374151' }}>
                The full editor (add/edit/delete SOPs, upload videos/audio/PDFs, CSV import/export, role-based assignment) lives inside each outlet.
                Click any outlet below to jump straight to its SOPs tab.
              </p>
            </div>
          </div>
        </div>

        {/* Hero */}
        <div className="border-b pb-6 mb-6" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex items-center gap-2 mb-2">
            <BookOpenCheck size={22} style={{ color: '#2A7B88' }} />
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: '#1B2A4A' }}>MindEscapes® Training Library</h2>
          </div>
          <p className="text-[13px] leading-relaxed" style={{ color: '#374151' }}>
            The complete written standard for every role, every shift, every department — live in every team member's pocket.
          </p>
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7">
          <StatCard label="Unique Modules" value={data.uniqueModules} accent="#2A7B88" />
          <StatCard label="Total SOP Records" value={data.totalRecords} accent="#1B2A4A" />
          <StatCard label="Outlets Covered" value={data.perOutlet.length} accent="#BA7517" />
          <StatCard label="Media Attachments" value={data.mediaCount} accent="#2D8B5E" />
        </div>

        {/* Categories breakdown */}
        <h3 className="text-sm font-bold mb-3" style={{ color: '#1B2A4A' }}>By Category</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mb-7">
          {data.categories.map(([cat, n]) => (
            <div key={cat} className="flex justify-between items-center text-[12px] py-1 border-b" style={{ borderColor: '#F3F4F6' }}>
              <span style={{ color: '#374151' }}>{cat}</span>
              <span className="font-semibold tabular-nums" style={{ color: '#1B2A4A' }}>{n}</span>
            </div>
          ))}
        </div>

        {/* Per outlet */}
        <h3 className="text-sm font-bold mb-3" style={{ color: '#1B2A4A' }}>
          SOPs Per Outlet
          <span className="text-[11px] font-normal ml-2 print:hidden" style={{ color: '#2A7B88' }}>
            <Pencil size={10} className="inline mr-0.5" /> click to edit
          </span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
          {data.perOutlet.map((o) => (
            <button key={o.name}
              onClick={() => navigate(`/division/me/outlet/${encodeURIComponent(o.name)}?tab=sops`)}
              className="flex justify-between items-center p-3 rounded-md border hover:border-[#2A7B88] hover:bg-[#F7F5F2] transition-colors text-left group print:hover:bg-transparent print:border-[#E5E1DC]"
              style={{ borderColor: '#E5E1DC' }}
              data-testid={`launch-outlet-${o.name}`}>
              <div className="min-w-0">
                <p className="text-[12px] font-medium truncate" style={{ color: '#1B2A4A' }}>{o.name}</p>
                <p className="text-[10px] print:hidden" style={{ color: '#6B7280' }}>Tap to add/edit articles</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-lg font-bold tabular-nums" style={{ color: '#2A7B88' }}>{o.total}</span>
                <ChevronRight size={14} className="print:hidden group-hover:translate-x-0.5 transition-transform" style={{ color: '#2A7B88' }} />
              </div>
            </button>
          ))}
        </div>

        {/* Talking points for briefing */}
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#FFFBEB' }}>
          <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: '#BA7517' }}>Talking Points for Morning Briefing</p>
          <ul className="space-y-1.5 text-[12px] leading-relaxed" style={{ color: '#374151' }}>
            <li>• Every one of you — kitchen, service, bar, housekeeping, security, gardens, maintenance, front desk — now has their complete training library on their phone.</li>
            <li>• Open ScaleBoard → your outlet → SOPs. Everything's there: read the doc, watch the training video, listen to the audio.</li>
            <li>• Your manager will assign you your day-1 and week-1 modules. Tap "Mark as Read" after you've studied each one.</li>
            <li>• This is how MindEscapes becomes what Oberoi became — a written, specific, live-able standard every shift.</li>
          </ul>
        </div>

        {/* Closing */}
        <div className="mt-7 pt-5 border-t text-center" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[11px] italic" style={{ color: '#6B7280' }}>
            "We are what we repeatedly do. Excellence, then, is not an act, but a habit." — Aristotle
          </p>
          <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>
            MindEscapes® Training Library · Live in ScaleBoard · {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
      <p className="text-[9px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{label}</p>
      <p className="text-2xl font-bold tabular-nums mt-0.5" style={{ color: accent }}>{value}</p>
    </div>
  );
}
